import type {
  PublicPaymentCapabilities,
  PublicTableItem,
  PublicTableItemModifier,
  PublicTableResponse,
} from '@/lib/api/public';
import type { MenuAvailabilityState } from '@/types/menuAvailability';

const createAvailabilityState = (): MenuAvailabilityState => ({
  status: 'available',
  isAvailable: true,
  reasons: [],
  nextAvailableAt: null,
  currentWindow: null,
  upcomingWindows: [],
  soldOutUntil: null,
  autoExpireAt: null,
  categoryAutoExpireAt: null,
});

const withDietaryTags = (item: PublicTableItem, tags?: string[]): PublicTableItem => {
  if (tags && tags.length > 0) {
    (item as unknown as { dietary_tags: string[] }).dietary_tags = tags;
  }
  return item;
};

const withMetadata = (
  item: PublicTableItem,
  metadata?: Partial<{
    prepTime: string;
    rating: number;
    reviewCount: number;
    dietaries: string[];
    modifiers: Array<Partial<PublicTableItemModifier>>;
    isPopular: boolean;
  }>,
): PublicTableItem => {
  if (metadata) {
    (item as unknown as { metadata: typeof metadata }).metadata = metadata;
  }
  return item;
};

const createItem = (base: Partial<PublicTableItem> & { id: string; category_id: string; name: string }): PublicTableItem => ({
  id: base.id,
  category_id: base.category_id,
  name: base.name,
  description: base.description ?? null,
  price_cents: base.price_cents ?? 0,
  is_available: base.is_available ?? true,
  availability: base.availability ?? createAvailabilityState(),
  image_url: base.image_url ?? null,
  image_alt_text: base.image_alt_text ?? null,
  image_spice_level: base.image_spice_level ?? null,
  modifiers: base.modifiers ?? [],
});

const withRatings = (item: PublicTableItem, rating?: number, reviewCount?: number): PublicTableItem => {
  if (typeof rating === 'number') {
    (item as PublicTableItem & { average_rating?: number }).average_rating = rating;
  }
  if (typeof reviewCount === 'number') {
    (item as PublicTableItem & { review_count?: number }).review_count = reviewCount;
  }
  return item;
};

export interface DemoMenuOptions {
  tenantSlug: string;
  tableSlug: string;
}

export const buildDemoTableResponse = ({ tenantSlug, tableSlug }: DemoMenuOptions): PublicTableResponse => {
  const burgers = withDietaryTags(
    withRatings(
      withMetadata(
        createItem({
          id: 'demo-smash-burger',
          category_id: 'burgers',
          name: 'Smash Burger',
          description: 'Double smashed beef patty, caramelised onions, cheddar and house pickles.',
          price_cents: 1499,
          image_url: 'https://images.unsplash.com/photo-1553979459-d2229ba7433b?auto=format&fit=crop&w=1200&q=80',
          modifiers: [
            { id: 'cheese', name: 'Extra cheese', price_delta_cents: 200 },
            { id: 'bacon', name: 'Applewood bacon', price_delta_cents: 350 },
          ],
        }),
        {
          prepTime: '12 min',
          rating: 4.8,
          reviewCount: 238,
          modifiers: [
            { modifier_id: 'cheese', name: 'Extra cheese', price_delta_cents: 200 },
            { modifier_id: 'bacon', name: 'Applewood bacon', price_delta_cents: 350 },
          ],
          isPopular: true,
        },
      ),
      4.8,
      238,
    ),
    ['contains-gluten'],
  );

  const veggieBurger = withDietaryTags(
    withRatings(
      withMetadata(
        createItem({
          id: 'demo-veggie-burger',
          category_id: 'burgers',
          name: 'Charred Veggie Burger',
          description: 'Smoked beet patty, avocado smash, arugula and tomato jam on brioche.',
          price_cents: 1399,
          image_url: 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?auto=format&fit=crop&w=1200&q=80',
          modifiers: [
            { id: 'gf-bun', name: 'Gluten free bun', price_delta_cents: 150 },
            { id: 'truffle-fries', name: 'Upgrade to truffle fries', price_delta_cents: 450 },
          ],
        }),
        {
          prepTime: '14 min',
          rating: 4.6,
          reviewCount: 164,
          dietaries: ['vegetarian'],
        },
      ),
      4.6,
      164,
    ),
    ['vegetarian'],
  );

  const tacos = withDietaryTags(
    withRatings(
      withMetadata(
        createItem({
          id: 'demo-birria-tacos',
          category_id: 'tacos',
          name: 'Slow Braised Birria Tacos',
          description: 'Corn tortillas, braised beef, consommé, lime crema and pickled onions.',
          price_cents: 1599,
          image_url: 'https://images.unsplash.com/photo-1608033411454-9bbee6f0b985?auto=format&fit=crop&w=1200&q=80',
        }),
        {
          prepTime: '18 min',
          rating: 4.9,
          reviewCount: 312,
          isPopular: true,
        },
      ),
      4.9,
      312,
    ),
    ['contains-dairy'],
  );

  const jackfruitTacos = withDietaryTags(
    withMetadata(
      createItem({
        id: 'demo-jackfruit-tacos',
        category_id: 'tacos',
        name: 'Adobo Jackfruit Tacos',
        description: 'Charred jackfruit, crispy chickpeas, cilantro slaw and lime.',
        price_cents: 1399,
        image_url: 'https://images.unsplash.com/photo-1553621042-f6e147245754?auto=format&fit=crop&w=1200&q=80',
      }),
      {
        prepTime: '16 min',
        dietaries: ['vegan'],
      },
    ),
    ['vegan'],
  );

  const salad = withDietaryTags(
    withMetadata(
      createItem({
        id: 'demo-kale-salad',
        category_id: 'salads',
        name: 'Citrus Crunch Kale',
        description: 'Tuscan kale, orange supremes, toasted almonds, chia seeds and citrus vinaigrette.',
        price_cents: 1199,
        image_url: 'https://images.unsplash.com/photo-1505252585461-04db1eb84625?auto=format&fit=crop&w=1200&q=80',
      }),
      {
        prepTime: '6 min',
        dietaries: ['vegan', 'gluten-free'],
      },
    ),
    ['vegan', 'gluten-free'],
  );

  const pizza = withRatings(
    withMetadata(
      createItem({
        id: 'demo-margherita-pizza',
        category_id: 'pizza',
        name: 'Heirloom Margherita',
        description: 'Blistered sourdough crust, buffalo mozzarella, basil oil and parm.',
        price_cents: 1899,
        image_url: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80',
      }),
      {
        prepTime: '20 min',
        rating: 4.7,
        reviewCount: 189,
      },
    ),
    4.7,
    189,
  );

  const sparkling = withDietaryTags(
    createItem({
      id: 'demo-yuzu-spritz',
      category_id: 'drinks',
      name: 'Yuzu Spritz',
      description: 'House yuzu cordial, sparkling water, charred rosemary.',
      price_cents: 599,
      image_url: 'https://images.unsplash.com/photo-1581579186989-4c08b39cc61c?auto=format&fit=crop&w=1200&q=80',
    }),
    ['vegan'],
  );

  const sundae = withDietaryTags(
    withMetadata(
      createItem({
        id: 'demo-smores-sundae',
        category_id: 'desserts',
        name: "Tabletop S'mores Sundae",
        description: 'Toasted marshmallow gelato, cocoa nib crumble, smoked caramel.',
        price_cents: 899,
        image_url: 'https://images.unsplash.com/photo-1543353071-10c8ba85a904?auto=format&fit=crop&w=1200&q=80',
      }),
      {
        prepTime: '5 min',
        rating: 4.5,
      },
    ),
    ['contains-dairy'],
  );

  return {
    table: {
      id: `demo-table-${tableSlug || 'default'}`,
      qr_slug: tableSlug || 'demo-table',
      table_number: 'D12',
    },
    tenant: {
      id: `demo-tenant-${tenantSlug || 'default'}`,
      slug: tenantSlug || 'demo',
      name: 'Harvest & Hearth',
      currency: 'USD',
    },
    location: {
      id: 'demo-location',
      name: 'Market Street',
      currency: 'USD',
    },
    categories: [
      { id: 'burgers', name: 'Burgers', sort_order: 1 },
      { id: 'tacos', name: 'Tacos', sort_order: 2 },
      { id: 'salads', name: 'Salads', sort_order: 3 },
      { id: 'pizza', name: 'Pizza', sort_order: 4 },
      { id: 'drinks', name: 'Drinks', sort_order: 5 },
      { id: 'desserts', name: 'Desserts', sort_order: 6 },
    ],
    items: [burgers, veggieBurger, tacos, jackfruitTacos, salad, pizza, sparkling, sundae],
    pricing: {
      currency: 'USD',
      tax_rate_bps: 825,
      service_charge_bps: 180,
    },
    scheduling: {
      timezone: 'America/Los_Angeles',
      windows: [
        { id: 'now', label: 'Ready in ~15 min', start: Date.now(), end: Date.now() + 20 * 60 * 1000 },
        { id: 'evening', label: 'Tonight 7:00 PM', start: Date.now() + 4 * 60 * 60 * 1000, end: Date.now() + 5 * 60 * 60 * 1000 },
      ],
    },
    loyalty: {
      status: 'enabled',
      earnMode: 'per_spend',
      pointsPerCurrency: 1,
      currencyUnit: 100,
      pointsPerOrder: 0,
      redemption: {
        enabled: true,
        pointsPerUnit: 100,
        currencyUnit: 100,
        minPoints: 50,
        maxPercent: 50,
      },
    },
    guest_profile: {
      external_id: null,
      name: 'Taylor Guest',
      loyalty_points: 120,
      favorite_item_ids: ['demo-smash-burger', 'demo-margherita-pizza'],
      last_order_snapshot: null,
      consent: {
        personalization: true,
        schedule: false,
        notifications: { email: true },
      },
      taste_profile: {
        tags: ['spicy', 'smoky'],
        heat: 'medium',
        tagScores: {
          spicy: 1,
          smoky: 0.65,
        },
        cuisineAffinity: {
          mexican: 0.9,
          thai: 0.55,
        },
        cuisines: ['mexican'],
        updatedAt: Date.now() - 6 * 60 * 60 * 1000,
        source: 'stored',
      },
      reward: {
        type: 'complimentary_drink',
        description: 'Welcome back! Enjoy a complimentary drink on us.',
        token: 'demo-reward-token',
      },
    },
    cacheStatus: 'network',
    cachedAt: Date.now(),
  };
};

export const DEMO_PAYMENT_CAPABILITIES: PublicPaymentCapabilities = {
  provider: 'stripe',
  publishableKey: null,
  wallets: {
    applePay: false,
    googlePay: false,
  },
};

